import { ReactNode, useRef, type MutableRefObject } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import DOMPurify from "dompurify";
import { convertDate } from "../../../services/luxonHelper";
import { t } from "@i18n";
import { useSelector } from "react-redux";
import { GanttStatic, Task } from "./dhtmlxgantt";
import { GLOBAL } from "@Services/constants";
import { GanttTask, ResourceAssignment, ResourceItem } from "@WebTypes/scheduleDomain";
import { RootState } from "@Reducers/index";
import InfoIcon from "@Assets/icons/svg/details.svg?react";
declare const gantt: GanttStatic;

const renderSanitized = (node: ReactNode): string => {
  if (node === null || node === undefined || node === false) return "";
  return DOMPurify.sanitize(renderToStaticMarkup(<>{node}</>));
};

/**
 * Escapes HTML special characters to prevent XSS attacks.
 * Use this when inserting user-controlled data into HTML templates.
 */
const escapeHtml = (str: unknown): string => {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

export function resolveTaskResources(task: Task): ResourceAssignment[] {
  const resourceStore = gantt.getDatastore("resource");
  const assignments: ResourceAssignment[] = task["$resourceAssignments"] ?? [];
  return assignments.map((assignment) => {
    const resource = (resourceStore.getItem(assignment.resource_id) || {}) as ResourceItem;
    return {
      ...assignment,
      text: assignment.text ?? resource.text,
      counter: assignment.counter ?? resource.counter,
      availability: assignment.availability ?? resource.availability,
      cost: assignment.cost ?? resource.cost,
      type: assignment.type ?? resource.type,
    };
  });
}

const useGanttHelper = (
  ganttConfig?: any,
  timeZone?: string,
  contractId?: string,
  scheduleVersionId?: string,
  isGroupedRef?: MutableRefObject<boolean>,
) => {
  const { GanttDetails } = useSelector((state: RootState) => state);
  const { currencyDetail, symbolDetail } = useSelector((state: RootState) => state.currencyDetails);

  const header_height = 90;
  const row_height = 48;
  const defaultHoursPerDay = 24;
  const P6 = "P6";
  const MSP = "MSP";
  const SupportedExtension = ".mpp,.xer";
  const XER = "xer";
  const MPP = "mpp";
  const HOUR = "hour";

  const getHoursPerDay = (task: any): number => {
    const calendarId = task?.calendar_id ?? GLOBAL;
    const calendar = gantt.getCalendar(calendarId);
    let totalHours = 0;

    if (calendar?._worktime?.parsed?.hours) {
      for (const hour of calendar._worktime.parsed.hours) {
        totalHours = totalHours + hour.durationHours;
      }
    }

    return totalHours || defaultHoursPerDay;
  };

  const formatCost = (value: number | string) => {
    const currencySymbol = symbolDetail?.name ?? "";
    const amount = Number((Number(value) || 0).toFixed(2));
    if (!currencySymbol) return `${amount}`;
    return currencyDetail?.category === "Suffix" ? `${amount}${currencySymbol}` : `${currencySymbol}${amount}`;
  };

  const ganttLayout = {
    css: "gantt_container",
    rows: [
      {
        cols: [
          {
            rows: [
              {
                view: "grid",
                scrollable: true,
                scrollX: "gridScrollHor",
                scrollY: "sharedScrollVer",
              },
              {
                view: "scrollbar",
                id: "gridScrollHor",
                scroll: "x",
                group: "hor",
              }, // Independent horizontal scroll
            ],
          },
          { resizer: true, width: 2 },
          {
            rows: [
              {
                view: "timeline",
                scrollX: "timelineScrollHor",
                scrollY: "sharedScrollVer",
              },
              {
                view: "scrollbar",
                id: "timelineScrollHor",
                scroll: "x",
                group: "hor",
              }, // Independent horizontal scroll
            ],
          },
          { view: "scrollbar", id: "sharedScrollVer", scroll: "y" }, // Shared vertical scrollbar
        ],
      },
    ],
  };

  const ganttWithResourceLayout = {
    css: "gantt_container",
    rows: [
      {
        cols: [
          {
            rows: [
              {
                view: "grid",
                scrollable: true,
                scrollX: "gridScrollHor",
                scrollY: "sharedScrollVer",
              },
              {
                view: "scrollbar",
                id: "gridScrollHor",
                scroll: "x",
                group: "hor",
              }, // Independent horizontal scroll
            ],
          },
          { resizer: true, width: 2 },
          {
            rows: [
              {
                view: "timeline",
                scrollX: "timelineScrollHor",
                scrollY: "sharedScrollVer",
              },
              {
                view: "scrollbar",
                id: "timelineScrollHor",
                scroll: "x",
                group: "hor",
              }, // Independent horizontal scroll
            ],
          },
          { view: "scrollbar", id: "sharedScrollVer", scroll: "y" }, // Shared vertical scrollbar
        ],
      },
      { resizer: true, width: 1 },
      {
        config: {
          columns: [
            {
              name: "name",
              label: t("Name"),
              tree: true,
              resize: true,
              template(resource: any) {
                return resource.text;
              },
            },
            {
              name: "workload",
              label: t("Workload"),
              template(resource: any) {
                let totalResourceAssigned = 0;
                let totalUnitsAssigned = 0;
                const allTasks = gantt.getTaskByTime();
                for (const task of allTasks) {
                  if (task["$resourceAssignments"]?.length) {
                    for (const taskResource of task["$resourceAssignments"]) {
                      if (taskResource.resource_id === resource.id) {
                        const availability = Number(taskResource.availability ?? 0);
                        if (resource.type === "material") {
                          totalUnitsAssigned = totalUnitsAssigned + availability;
                        } else {
                          totalResourceAssigned = totalResourceAssigned + availability * Number(task.duration);
                        }
                      }
                    }
                  }
                }

                if (resource.type == "material") {
                  return Number.isInteger(totalUnitsAssigned) ? totalUnitsAssigned : totalUnitsAssigned.toFixed(2);
                }
                return `${(totalResourceAssigned / 100) * 8}h`;
              },
            },
          ],
        },
        cols: [
          { view: "resourceGrid", group: "grids", scrollY: "resourceVScroll" },
          { resizer: true, width: 1 },
          {
            view: "resourceTimeline",
            scrollX: "timelineScrollHor",
            scrollY: "resourceVScroll",
          },
          { view: "scrollbar", id: "resourceVScroll", group: "vertical" },
        ],
        gravity: 1,
      },
      { view: "scrollbar", id: "scrollHor" },
    ],
  };

  const ganttConfigs = {
    sort: true,
    date_format: "%m-%d-%Y %H:%i",
    fit_tasks: true,
    order_branch: !scheduleVersionId,
    order_branch_free: true,
    keep_grid_width: false,
    grid_resize: true,
    grid_elastic_columns: true,
    details_on_dblclick: false,
    work_time: true,
    inherit_calendar: true,
    project_resolve: true,
    show_unscheduled: false,
    project_calendar: true,
    start_date: null,
    end_date: null,
    timeline_placeholder: true,
    scales: [
      {
        unit: "day",
        step: 1,
        format: function (date: any) {
          if (date.getDay() === 0) {
            return gantt.date.date_to_str("%M %d")(date);
          }
          return "";
        },
      },
      {
        unit: "day",
        step: 1,
        format: function (date: any) {
          const dayLetters = ["S", "M", "T", "W", "T", "F", "S"];
          return dayLetters[date.getDay()];
        },
      },
    ],
    row_height,
    bar_height: 20,
    scale_height: header_height,
    smart_rendering: true,
    resources: true,
    correct_work_time: true,
    duration_unit: "day",
  };

  const infoTemplate = (task: Task) => {
    if (task?.haveBoqItems) {
      return (
        <InfoIcon
          data-task-id={task.id}
          className="gantt-task-info"
          style={{
            cursor: "pointer",
            marginTop: "0.75rem",
          }}
          title={t("linked BOQ Items")}
        />
      );
    }
  };

  const IDTemplate = (task: Task) => {
    if (task.calendar_id && GanttDetails.worktimes) {
      const calendar = GanttDetails.worktimes.find((calendar) => calendar.calendarUniqueId === task.calendar_id);

      return (
        <div className="flex items-center gap-2">
          <div
            style={{
              width: "0.1875rem",
              minWidth: "0.1875rem",
              height: `${(row_height - 4) / 16}rem`,
              minHeight: `${(row_height - 4) / 16}rem`,
              backgroundColor: calendar?.color,
              margin: "0",
              padding: "0",
              flexShrink: 0,
            }}
          />
          {task.TaskDisp}
        </div>
      );
    }

    return task.TaskDisp;
  };

  const ganttColumns = [
    { name: "add", width: 44, resize: true, hide: true },
    {
      name: "CustomIcon",
      align: "center",
      label: renderSanitized(<InfoIcon />),
      width: 52,
      resize: true,
      hide: !contractId,
      template(task: Task) {
        return renderSanitized(infoTemplate(task));
      },
    },
    {
      name: "TaskDisp",
      align: "left",
      label: t("ID"),
      width: 100,
      resize: true,
      hide: false,
      template(task: Task) {
        return renderSanitized(IDTemplate(task));
      },
    },
    {
      name: "text",
      label: t("Activity"),
      align: "left",
      width: 200,
      resize: true,
      tree: true,
      hide: false,
      editor: { type: "text", map_to: "text" },
      template(task: Task) {
        return escapeHtml(task.text);
      },
    },
    {
      name: "start_date",
      align: "left",
      width: 200,
      resize: true,
      label: t("Start Date"),
      hide: false,
      editor: { type: "custom_datetime", map_to: "start_date" },
      template(task: Task) {
        const dateFormat = task.start_date
          ? convertDate(new Date(task.start_date).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
    },
    {
      name: "end_date",
      align: "left",
      width: 200,
      resize: true,
      label: t("End Date"),
      hide: false,
      template(task: Task) {
        const dateFormat = task.end_date
          ? convertDate(new Date(task.end_date).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
      editor: { type: "custom_datetime", map_to: "end_date" },
    },
    {
      name: "duration",
      align: "left",
      width: 80,
      resize: true,
      sort: true,
      label: t("Duration"),
      hide: false,
      template(task: Task) {
        if (ganttConfig.current == HOUR) {
          return Math.ceil((task.duration || 0) / getHoursPerDay(task));
        }
        return task.duration;
      },
      editor: { type: "number", map_to: "duration", min: 0 },
    },
    {
      name: "cost",
      align: "left",
      width: 80,
      resize: true,
      sort: true,
      label: t("Planned Value"),
      hide: true,
      template(task: Task) {
        return formatCost(task.cost ?? 0);
      },
      editor: { type: "number", map_to: "cost", min: 0 },
    },
    {
      name: "actualCost",
      align: "left",
      width: 80,
      resize: true,
      sort: true,
      label: t("Actual Cost"),
      hide: true,
      template(task: Task) {
        return formatCost(task.actualCost ?? 0);
      },
      editor: { type: "number", map_to: "actualCost", min: 0 },
    },
    {
      name: "earnedValue",
      align: "left",
      width: 80,
      resize: true,
      sort: true,
      label: t("Earned Value"),
      hide: true,
      template(task: Task) {
        return formatCost((task.cost ?? 0) * (task.progress ?? 0));
      },
    },
    {
      name: "work%Complete",
      align: "left",
      width: 150,
      resize: true,
      label: t("Work Percentage Complete"),
      hide: !(process.env.REACT_APP_ENV === "prod" && globalThis.location.host === "prestige.app.cube.ms"),
      template(task: Task) {
        let workCompleted = 0;
        if (gantt.isSummaryTask(task)) {
          const children = gantt.getChildren(task.id);
          const childTasks = children.map((id: any) => gantt.getTask(id));
          let plannedWork = 0,
            actualWork = 0;
          for (const task of childTasks) {
            if (task["$resourceAssignments"]?.length) {
              for (const resource of task["$resourceAssignments"]) {
                const availability = Number(resource.availability ?? 0);
                const actualWorkHours = Number(resource.actualResourceWork ?? 0);
                const plannedWorkHours = ((availability * Number(task.duration)) / 100) * 8;

                if (actualWorkHours > plannedWorkHours) {
                  plannedWork = plannedWork + actualWorkHours;
                  actualWork = actualWork + actualWorkHours;
                } else {
                  plannedWork = plannedWork + plannedWorkHours;
                  actualWork = actualWork + actualWorkHours;
                }
              }
            }
          }

          if (plannedWork > 0) {
            workCompleted = (actualWork / plannedWork) * 100;
          }
        } else if (task["$resourceAssignments"]?.length) {
          let plannedWork = 0,
            actualWork = 0;
          for (const resource of task["$resourceAssignments"]) {
            const availability = Number(resource.availability ?? 0);
            const actualWorkHours = Number(resource.actualResourceWork ?? 0);
            const plannedWorkHours = ((availability * Number(task.duration)) / 100) * 8;

            if (actualWorkHours > plannedWorkHours) {
              plannedWork = plannedWork + actualWorkHours;
              actualWork = actualWork + actualWorkHours;
            } else {
              plannedWork = plannedWork + plannedWorkHours;
              actualWork = actualWork + actualWorkHours;
            }
          }
          if ((actualWork / plannedWork) * 100 > 100) {
            workCompleted = 100;
          } else {
            workCompleted = (actualWork / plannedWork) * 100;
          }
        }
        return workCompleted.toFixed(2);
      },
    },
    {
      name: "actualStartDate",
      align: "left",
      width: 200,
      resize: true,
      label: t("Actual Start Date"),
      hide: true,
      template(task: Task) {
        const dateFormat = task.actualStartDate
          ? convertDate(new Date(task.actualStartDate).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
      editor: { type: "custom_datetime", map_to: "actualStartDate" },
    },
    {
      name: "actualEndDate",
      align: "left",
      width: 200,
      resize: true,
      label: t("Actual End Date"),
      hide: true,
      template(task: Task) {
        const dateFormat = task.actualEndDate
          ? convertDate(new Date(task.actualEndDate).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
      editor: { type: "custom_datetime", map_to: "actualEndDate" },
    },
    {
      name: "actualDuration",
      align: "left",
      resize: true,
      width: 200,
      label: t("Actual Duration"),
      sort: true,
      hide: true,
      template(task: Task) {
        if (task.actualStartDate && task.actualEndDate) {
          task.actualDuration = gantt.calculateDuration({
            start_date: new Date(task.actualStartDate),
            end_date: new Date(task.actualEndDate),
            task,
          });
          if (ganttConfig.current == HOUR) {
            return Math.ceil(task.actualDuration / getHoursPerDay(task));
          }
          return task.actualDuration;
        }
        return "";
      },
    },
    {
      name: "plannedStartDate",
      align: "left",
      width: 200,
      resize: true,
      label: t("Planned Start Date"),
      hide: true,
      template(task: Task) {
        const dateFormat = task.plannedStartDate
          ? convertDate(new Date(task.plannedStartDate).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
      editor: { type: "custom_datetime", map_to: "plannedStartDate" },
    },
    {
      name: "plannedEndDate",
      align: "left",
      width: 200,
      resize: true,
      label: t("Planned End Date"),
      hide: true,
      template(task: Task) {
        const dateFormat = task.plannedEndDate
          ? convertDate(new Date(task.plannedEndDate).getTime(), "dateForSchedule", timeZone)
          : "";
        return dateFormat;
      },
      editor: { type: "custom_datetime", map_to: "plannedEndDate" },
    },
    {
      name: "plannedDuration",
      align: "left",
      resize: true,
      width: 200,
      label: t("Planned Duration"),
      sort: true,
      hide: true,
      template(task: Task) {
        if (ganttConfig.current == HOUR) {
          return Math.ceil(task.plannedDuration / getHoursPerDay(task));
        }
        return task.plannedDuration;
      },
    },
    {
      name: "remDuration",
      align: "left",
      resize: true,
      width: 200,
      label: t("Remaining Duration"),
      sort: false,
      hide: true,
      template(task: Task) {
        if (ganttConfig.current == HOUR) {
          if (task.remDuration == null) {
            return Math.ceil(task.plannedDuration / getHoursPerDay(task));
          } else {
            return Math.ceil(task.remDuration / getHoursPerDay(task));
          }
        }
        if (task.remDuration == null) {
          return task.plannedDuration;
        } else {
          return task.remDuration;
        }
      },
    },
    {
      name: "progress",
      label: t("Progress"),
      width: 100,
      resize: true,
      align: "left",
      sort: false,
      hide: true,
      template(task: Task) {
        return `${Math.round((task.progress || 0) * 100)} %`;
      },
      editor: { type: "number", map_to: "progress", min: 0 },
    },
    {
      name: "totalSlack",
      align: "left",
      resize: true,
      width: 100,
      label: t("Total slack"),
      sort: false,
      hide: true,
      template(task: Task) {
        if (task.actualStartDate && task.actualEndDate) {
          return "0";
        } else if (task.totalSlack) {
          if (ganttConfig.current == HOUR) {
            return Math.ceil(task.totalSlack / getHoursPerDay(task));
          }
          return task.totalSlack;
        }
        if (task.type === "project" && !gantt.hasChild(task.id)) {
          return "0";
        }
        const slack = gantt.getTotalSlack(task);
        if (ganttConfig.current == HOUR) {
          return Math.ceil(slack / getHoursPerDay(task));
        }
        return slack;
      },
    },
    {
      name: "critical",
      align: "left",
      resize: true,
      width: 100,
      label: t("Critical"),
      sort: false,
      hide: true,
      template(task: Task) {
        if (task.critical) {
          return task.critical == "1" ? "Yes" : "No";
        }
        return gantt.isCriticalTask(task) ? "Yes" : "No";
      },
    },
    {
      name: "planned%",
      align: "left",
      resize: true,
      width: 100,
      label: t("Planned %"),
      sort: false,
      hide: true,
      template(task: Task) {
        let value: number = 0;
        const statusDate = GanttDetails.statusDate ? new Date(GanttDetails.statusDate) : new Date();
        if (task.plannedStartDate && task.plannedEndDate) {
          const differenceInDays = gantt.calculateDuration({
            start_date: task.plannedStartDate,
            end_date: statusDate,
            task: task,
          });

          const plannedDurationInDays = gantt.calculateDuration({
            start_date: task.plannedStartDate,
            end_date: task.plannedEndDate,
            task: task,
          });
          if (
            new Date(task.plannedEndDate).getTime() <= statusDate.getTime() ||
            (new Date(task.plannedStartDate).getTime() <= statusDate.getTime() && plannedDurationInDays == 0)
          ) {
            value = 100;
          } else if (
            new Date(task.plannedStartDate).getTime() <= statusDate.getTime() &&
            new Date(task.plannedEndDate).getTime() >= statusDate.getTime() &&
            plannedDurationInDays > 0
          ) {
            value = (differenceInDays * 100) / plannedDurationInDays;
          }
        }
        return `${value.toFixed(2)} %`;
      },
    },
    {
      name: "%Complete",
      align: "left",
      resize: true,
      width: 100,
      label: t("% Complete"),
      sort: false,
      hide: !(process.env.REACT_APP_ENV === "prod" && globalThis.location.host === "prestige.app.cube.ms"),
      template(task: Task) {
        if (!task.actualStartDate) {
          return "0 %";
        }
        const actualDuration = gantt.calculateDuration({
          start_date: task.actualStartDate,
          end_date: GanttDetails.statusDate ? new Date(GanttDetails.statusDate) : new Date(),
          task: task,
        });
        const totalDuration = gantt.calculateDuration({
          start_date: task.start_date!,
          end_date: task.end_date!,
          task: task,
        });

        if (actualDuration <= 0) {
          return "0 %";
        }

        if ((task.actualStartDate && task.actualEndDate) || actualDuration > totalDuration) {
          return "100 %";
        }

        if (totalDuration > 0) {
          const progress = (actualDuration / totalDuration) * 100;
          return `${progress.toFixed(2)} %`;
        }

        return "0 %";
      },
    },
    {
      name: "startVariance",
      align: "left",
      resize: true,
      width: 200,
      label: t("Start Variance"),
      sort: false,
      hide: true,
      template(task: Task) {
        if (task.start_date && task.plannedStartDate) {
          if (ganttConfig.current == HOUR) {
            const differenceInHours = gantt.calculateDuration({
              start_date: task.plannedStartDate,
              end_date: task.start_date,
              task: task,
            });
            return Math.ceil(differenceInHours / getHoursPerDay(task));
          }
          return gantt.calculateDuration({
            start_date: task.plannedStartDate,
            end_date: task.start_date,
            task: task,
          });
        }
      },
    },
    {
      name: "finishVariance",
      align: "left",
      resize: true,
      width: 200,
      label: t("Finish Variance"),
      sort: false,
      hide: true,
      template(task: Task) {
        if (task.end_date && task.plannedEndDate) {
          if (ganttConfig.current == HOUR) {
            const differenceInHours = gantt.calculateDuration({
              start_date: task.plannedEndDate,
              end_date: task.end_date,
              task: task,
            });
            return Math.ceil(differenceInHours / getHoursPerDay(task));
          }
          return gantt.calculateDuration({
            start_date: task.plannedEndDate,
            end_date: task.end_date,
            task: task,
          });
        }
      },
    },
    {
      name: "predecessor",
      align: "left",
      resize: true,
      width: 200,
      label: t("Predecessor"),
      sort: false,
      hide: true,
      template(task: Task) {
        const predecessor = (task.$target || []).map((linkId: any) => {
          const link = gantt.getLink(linkId);
          const predecessorTask = gantt.getTask(link.source);
          return escapeHtml(predecessorTask?.TaskDisp);
        });
        return predecessor.join(", ");
      },
    },
    {
      name: "successor",
      align: "left",
      resize: true,
      width: 200,
      label: t("Successor"),
      sort: false,
      hide: true,
      template(task: Task) {
        const successor = (task.$source || []).map((linkId: any) => {
          const link = gantt.getLink(linkId);
          const succesorTask = gantt.getTask(link.target);
          return escapeHtml(succesorTask?.TaskDisp);
        });
        return successor.join(", ");
      },
    },
  ];

  const ganttTemplates = {
    rightside_text(start: any, end: any, task: Task) {
      let resourceNames: string[] = [];
      if (!isGroupedRef?.current) {
        resourceNames = resolveTaskResources(task)
          .map((resource: ResourceAssignment) => resource.text)
          .filter((name: string | undefined): name is string => Boolean(name))
          .map((name: string) => escapeHtml(name));
      }
      return `
  <span class="flex items-center font-semibold text-base text-black ms-6">
    ${task.progress! > 0 ? `<span class="me-3">${Math.floor(task.progress! * 100)}%</span>` : ""}
    ${resourceNames.join(", ")}
  </span>
`;
    },

    timeline_cell_class(task: Task, date: any) {
      if (!gantt.isWorkTime(date)) return "week_end";
      return "";
    },

    resource_cell_class(start_date: any, _end_date: any, _resource: any, _tasks: Task[]) {
      if (!gantt.isWorkTime(start_date)) {
        return "";
      }
      return "flex items-center justify-center";
    },

    resource_cell_value(start_date: any, end_date: any, resource: any, tasks: Task[]) {
      if (!gantt.isWorkTime(start_date)) {
        return "";
      }

      let totalResourceAssigned = 0;
      for (const task of tasks) {
        const resourceAssignments = task["$resourceAssignments"];
        if (resourceAssignments?.length) {
          for (const taskResource of resourceAssignments) {
            if (taskResource.resource_id === resource.id) {
              totalResourceAssigned = totalResourceAssigned + Number(taskResource.availability ?? 0);
            }
          }
        }
      }

      let result = 0;
      let units = 0;
      for (const item of tasks) {
        const assignments = gantt.getResourceAssignments(resource.id, item.id);
        for (const assignment of assignments) {
          if (resource.type === "material") {
            units =
              units + Number(assignment.availability ? Number(assignment.availability) / Number(item.duration) : 0);
          } else {
            result = result + Number(assignment.availability ?? 0);
          }
        }
      }

      const isWithinCapacity =
        totalResourceAssigned <= Number(resource.availability ?? 0) || resource.type === "material";
      const bgClass = isWithinCapacity ? "bg-green-500" : "bg-red-500";
      const circleClass = `w-10 h-10 rounded-full text-[#fcf9f9] text-sm font-semibold flex flex-col items-center justify-center ${bgClass}`;

      if (resource.type == "material") {
        const materialValue = Number.isInteger(units) ? `${units}` : units.toFixed(2);
        return `<div class="${circleClass}" resourceTooltipValue="${materialValue}"><span class="truncate max-w-full px-1">${materialValue}</span></div>`;
      }
      const hours = (result / 100) * 8;
      const hoursDisplay = hours.toFixed(2);
      return `<div class="${circleClass}" resourceTooltipValue="${hoursDisplay}"><span class="truncate max-w-full px-1">${hoursDisplay}</span></div>`;
    },

    grid_folder() {
      return null;
    },

    grid_file() {
      return null;
    },

    task_class(start: any, end: any, task: Task) {
      if (task.type == "Project") return "hide_project_progress_drag";
    },
    task_text() {
      return ""; // Removes the text inside the task bar
    },

    tooltip_text() {
      return "";
    },
  };

  const summaryToDoMap = useRef<Map<string, number>>(new Map());
  const summaryDoneMap = useRef<Map<string, number>>(new Map());
  const summaryPlannedDatesMap = useRef<Map<string, { start: Date | null; end: Date | null }>>(new Map());
  const summaryActualDatesMap = useRef<Map<string, { start: Date | null; end: Date | null }>>(new Map());

  function toDate(value: unknown): Date | null {
    return value ? new Date(value as string | number | Date) : null;
  }

  function rollUpActualDates(
    children: Array<string | number>,
    cache: Map<string, { start: Date | null; end: Date | null }>,
  ) {
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    let allHaveEnd = children.length > 0;
    for (const childId of children) {
      const dates = cache.get(childId as string);
      if (dates?.start && (!minStart || dates.start < minStart)) minStart = dates.start;
      if (!dates?.end) allHaveEnd = false;
      else if (!maxEnd || dates.end > maxEnd) maxEnd = dates.end;
    }
    return { start: minStart, end: allHaveEnd ? maxEnd : null };
  }

  function calculateProgressAndPlannedDateForParentTasks() {
    const toDoMap = new Map<string, number>();
    const doneMap = new Map<string, number>();
    const childCount = new Map<string, number>();
    const processed = new Map<string, number>();
    const datesMap = new Map<string, { start: Date | null; end: Date | null }>();
    const actualDatesMap = new Map<string, { start: Date | null; end: Date | null }>();

    gantt.eachTask(function (task: GanttTask) {
      const id = task.id as string;
      const parentId = gantt.getParent(id);

      if (!gantt.hasChild(id)) {
        const duration = task.duration ?? 0;
        toDoMap.set(id, duration);
        doneMap.set(id, (task.progress ?? 0) * duration);
        datesMap.set(id, { start: task.plannedStartDate ?? null, end: task.plannedEndDate ?? null });
        actualDatesMap.set(id, { start: toDate(task.actualStartDate), end: toDate(task.actualEndDate) });

        let curParent = parentId;
        while (curParent && curParent !== 0 && gantt.isTaskExists(curParent)) {
          const pid = curParent as string;
          processed.set(pid, (processed.get(pid) ?? 0) + 1);

          if (processed.get(pid)! < (childCount.get(pid) ?? 0)) break;

          const parent = gantt.getTask(curParent);
          const children = gantt.getChildren(curParent);
          let totalToDo = 0;
          let totalDone = 0;
          let minStart: Date | null = null;
          let maxEnd: Date | null = null;
          for (const childId of children) {
            totalToDo += toDoMap.get(childId) ?? 0;
            totalDone += doneMap.get(childId) ?? 0;
            const dates = datesMap.get(childId);
            if (dates?.start && (!minStart || dates.start < minStart)) minStart = dates.start;
            if (dates?.end && (!maxEnd || dates.end > maxEnd)) maxEnd = dates.end;
          }

          parent.progress = totalToDo ? totalDone / totalToDo : 0;
          const parentDur = parent.duration ?? 0;
          toDoMap.set(pid, parentDur);
          doneMap.set(pid, parent.progress * parentDur);
          parent.plannedStartDate = minStart;
          parent.plannedEndDate = maxEnd;
          datesMap.set(pid, { start: minStart, end: maxEnd });
          if (minStart && maxEnd) {
            parent.plannedDuration = gantt.calculateDuration({ start_date: minStart, end_date: maxEnd, task: parent });
          }

          curParent = gantt.getParent(curParent);
        }
      } else {
        const children = gantt.getChildren(id);
        childCount.set(id, children.length);
      }
    });

    summaryToDoMap.current = toDoMap;
    summaryDoneMap.current = doneMap;
    summaryPlannedDatesMap.current = datesMap;
    summaryActualDatesMap.current = actualDatesMap;
    gantt.render();
  }

  function refreshParentSummaries(taskId: string): Array<{
    id: string;
    progress?: number;
    plannedStartDate?: Date | null;
    plannedEndDate?: Date | null;
    plannedDuration?: number;
    actualStartDate?: Date | null;
    actualEndDate?: Date | null;
  }> {
    const updatedTasks: Array<{
      id: string;
      progress?: number;
      plannedStartDate?: Date | null;
      plannedEndDate?: Date | null;
      plannedDuration?: number;
      actualStartDate?: Date | null;
      actualEndDate?: Date | null;
    }> = [];
    let parentId: string;

    if (gantt.isTaskExists(taskId) && !gantt.hasChild(taskId)) {
      const task = gantt.getTask(taskId);
      const childId = taskId as string;
      const dur = task.duration ?? 0;
      summaryToDoMap.current.set(childId, dur);
      summaryDoneMap.current.set(childId, (task.progress ?? 0) * dur);
      summaryPlannedDatesMap.current.set(childId, {
        start: task.plannedStartDate ?? null,
        end: task.plannedEndDate ?? null,
      });
      summaryActualDatesMap.current.set(childId, {
        start: toDate(task.actualStartDate),
        end: toDate(task.actualEndDate),
      });
      parentId = gantt.getParent(taskId).toString();
    } else {
      parentId = taskId;
    }

    while (parentId && parentId !== "0" && gantt.isTaskExists(parentId)) {
      const parent = gantt.getTask(parentId);

      const children = gantt.getChildren(parentId);
      let totalToDo = 0;
      let totalDone = 0;
      let minStart: Date | null = null;
      let maxEnd: Date | null = null;
      for (const cId of children) {
        totalToDo += summaryToDoMap.current.get(cId) ?? 0;
        totalDone += summaryDoneMap.current.get(cId) ?? 0;
        const dates = summaryPlannedDatesMap.current.get(cId);
        if (dates?.start && (!minStart || dates.start < minStart)) minStart = dates.start;
        if (dates?.end && (!maxEnd || dates.end > maxEnd)) maxEnd = dates.end;
      }

      const oldParentToDo = summaryToDoMap.current.get(parentId) ?? 0;
      const oldParentDone = summaryDoneMap.current.get(parentId) ?? 0;
      const oldProgress = oldParentToDo ? oldParentDone / oldParentToDo : 0;
      const newProgress = totalToDo ? totalDone / totalToDo : 0;
      const parentDur = parent.duration ?? 0;
      summaryToDoMap.current.set(parentId, parentDur);
      summaryDoneMap.current.set(parentId, newProgress * parentDur);

      const oldParentStart = summaryPlannedDatesMap.current.get(parentId)?.start ?? null;
      const oldParentEnd = summaryPlannedDatesMap.current.get(parentId)?.end ?? null;
      summaryPlannedDatesMap.current.set(parentId, { start: minStart, end: maxEnd });

      const actual = rollUpActualDates(children, summaryActualDatesMap.current);
      const oldActualStart = summaryActualDatesMap.current.get(parentId)?.start ?? null;
      const oldActualEnd = summaryActualDatesMap.current.get(parentId)?.end ?? null;
      summaryActualDatesMap.current.set(parentId, actual);

      const update: {
        id: string;
        progress?: number;
        plannedStartDate?: Date | null;
        plannedEndDate?: Date | null;
        plannedDuration?: number;
        actualStartDate?: Date | null;
        actualEndDate?: Date | null;
      } = { id: parentId };
      let hasChanges = false;

      if (newProgress !== oldProgress) {
        parent.progress = newProgress;
        update.progress = newProgress;
        hasChanges = true;
      }

      const startChanged = minStart?.getTime() !== oldParentStart?.getTime();
      const endChanged = maxEnd?.getTime() !== oldParentEnd?.getTime();
      if (startChanged || endChanged) {
        parent.plannedStartDate = minStart;
        parent.plannedEndDate = maxEnd;
        if (minStart && maxEnd) {
          parent.plannedDuration = gantt.calculateDuration({ start_date: minStart, end_date: maxEnd, task: parent });
        }
        update.plannedStartDate = minStart;
        update.plannedEndDate = maxEnd;
        update.plannedDuration = parent.plannedDuration;
        hasChanges = true;
      }

      const actualStartChanged = actual.start?.getTime() !== oldActualStart?.getTime();
      const actualEndChanged = actual.end?.getTime() !== oldActualEnd?.getTime();
      if (actualStartChanged || actualEndChanged) {
        parent.actualStartDate = actual.start;
        parent.actualEndDate = actual.end;
        update.actualStartDate = actual.start;
        update.actualEndDate = actual.end;
        hasChanges = true;
      }

      if (hasChanges) updatedTasks.push(update);

      parentId = gantt.getParent(parentId).toString();
    }
    gantt.render();

    return updatedTasks;
  }

  return {
    header_height,
    ganttLayout,
    ganttWithResourceLayout,
    ganttConfigs,
    ganttColumns,
    ganttTemplates,
    getHoursPerDay,
    P6,
    MSP,
    SupportedExtension,
    XER,
    MPP,
    calculateProgressAndPlannedDateForParentTasks,
    refreshParentSummaries,
    HOUR,
  };
};

export default useGanttHelper;
